import { useState, useEffect } from 'react';
import { Calendar, Clock, Phone, Mail, User, Plus, Edit, Trash2, Send, CheckCircle, Video, Building } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const AdminRDV = ({ user, activeModule = "b2b" }) => {
  const [rdvList, setRdvList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    selected_employee: null,
    employee_id: '',
    employee_name: '',
    employee_email: '',
    event_type: 'rdv', // 'rdv', 'entretien'
    rdv_type: '', // 'absence', 'performance', 'disciplinaire', 'autre'
    mode: 'presentiel', // 'presentiel', 'telephonique', 'visio'
    date: '',
    time: '',
    location: '',
    phone: '',
    comment: '',
    send_email: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rdvRes, empRes] = await Promise.all([
        axios.get(`${API}/rdv`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setRdvList(rdvRes.data);
      setEmployees(empRes.data.filter(e => e.role === 'employee' && e.status === 'approved'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      selected_employee: null,
      employee_id: '',
      employee_name: '',
      employee_email: '',
      event_type: 'rdv',
      rdv_type: '',
      mode: 'presentiel',
      date: '',
      time: '',
      location: '',
      phone: '',
      comment: '',
      send_email: true
    });
    setEditingRdv(null);
    setShowForm(false);
  };

  const handleSelectEmployee = (employeeId) => {
    const emp = employees.find(e => e.user_id === employeeId);
    if (emp) {
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
      setFormData(prev => ({
        ...prev,
        selected_employee: employeeId,
        employee_id: employeeId,
        employee_name: fullName,
        employee_email: emp.email || '',
        phone: emp.phone || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selected_employee: null,
        employee_id: '',
        employee_name: '',
        employee_email: '',
        phone: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.employee_id) {
      alert('Veuillez sélectionner un salarié');
      return;
    }
    if (!formData.date || !formData.time) {
      alert('Veuillez renseigner la date et l\'heure');
      return;
    }
    if (formData.send_email && !formData.employee_email) {
      alert('L\'employé n\'a pas d\'email. Désactivez l\'envoi d\'email ou ajoutez une adresse.');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        employee_id: formData.employee_id,
        employee_name: formData.employee_name,
        event_type: formData.event_type,
        rdv_type: formData.rdv_type || null,
        mode: formData.mode,
        date: formData.date,
        time: formData.time,
        phone: formData.phone || null,
        email: formData.employee_email || null,
        comment: formData.comment || null,
        send_email: formData.send_email
      };

      if (editingRdv) {
        await axios.put(`${API}/rdv/${editingRdv.rdv_id}`, dataToSend, { withCredentials: true });
        alert('✅ RDV modifié avec succès');
      } else {
        const response = await axios.post(`${API}/rdv`, dataToSend, { withCredentials: true });
        if (response.data.email_sent) {
          alert(`✅ RDV créé et email de convocation envoyé à ${formData.employee_email}`);
        } else {
          alert('✅ RDV créé avec succès');
        }
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save RDV:', error);
      alert('❌ Erreur: ' + (error.response?.data?.detail || 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rdv) => {
    // Trouver l'employé par son ID
    const emp = employees.find(e => e.user_id === rdv.employee_id);
    
    setFormData({
      selected_employee: rdv.employee_id,
      employee_id: rdv.employee_id,
      employee_name: rdv.employee_name,
      employee_email: rdv.email || (emp?.email || ''),
      event_type: rdv.event_type || 'rdv',
      rdv_type: rdv.rdv_type || '',
      mode: rdv.mode || 'presentiel',
      date: rdv.date || '',
      time: rdv.time || '',
      location: rdv.location || '',
      phone: rdv.phone || '',
      comment: rdv.comment || '',
      send_email: false // Ne pas renvoyer d'email lors de modification
    });
    setEditingRdv(rdv);
    setShowForm(true);
  };

  const handleDelete = async (rdvId) => {
    if (!window.confirm('Supprimer ce RDV ?')) return;
    try {
      await axios.delete(`${API}/rdv/${rdvId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to delete RDV:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      'rdv': 'RDV',
      'entretien': 'Entretien'
    };
    return labels[type] || type;
  };

  const getRdvTypeLabel = (type) => {
    const labels = {
      'absence': 'Absence',
      'performance': 'Performance',
      'disciplinaire': 'Disciplinaire',
      'autre': 'Autre'
    };
    return labels[type] || type || '';
  };

  const getModeIcon = (mode) => {
    switch(mode) {
      case 'telephonique': return <Phone className="w-4 h-4" />;
      case 'visio': return <Video className="w-4 h-4" />;
      default: return <Building className="w-4 h-4" />;
    }
  };

  const getModeLabel = (mode) => {
    const labels = {
      'presentiel': 'Présentiel',
      'telephonique': 'Téléphonique',
      'visio': 'Visioconférence'
    };
    return labels[mode] || mode;
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1D4E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">RDV / Entretiens</h1>
            <p className="text-gray-500 mt-1">{rdvList.length} rendez-vous enregistré(s)</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-[#1D4E89] hover:bg-[#163d6e] text-white"
            data-testid="new-rdv-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau RDV
          </Button>
        </div>

        {/* Formulaire */}
        {showForm && (
          <Card className="border-2 border-[#1D4E89] bg-blue-50">
            <CardHeader className="pb-4">
              <CardTitle className="text-[#1D4E89]">
                {editingRdv ? 'Modifier le RDV' : 'Nouveau RDV / Entretien'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Sélection employé */}
                <div className="space-y-2">
                  <Label className="font-semibold">Salarié *</Label>
                  <select
                    value={formData.selected_employee || ''}
                    onChange={(e) => handleSelectEmployee(e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white"
                    data-testid="employee-select"
                    required
                  >
                    <option value="">-- Sélectionner un salarié --</option>
                    {employees
                      .sort((a, b) => {
                        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.name;
                        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.name;
                        return nameA.localeCompare(nameB);
                      })
                      .map(emp => {
                        const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
                        return (
                          <option key={emp.user_id} value={emp.user_id}>
                            {fullName} {emp.email ? `(${emp.email})` : ''}
                          </option>
                        );
                      })}
                  </select>
                  {formData.selected_employee && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Mail className="w-3 h-3 text-blue-500" />
                      <span>{formData.employee_email || 'Pas d\'email'}</span>
                    </div>
                  )}
                </div>

                {/* Type d'événement */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-semibold">Type *</Label>
                    <select
                      value={formData.event_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, event_type: e.target.value }))}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="rdv">RDV</option>
                      <option value="entretien">Entretien</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold">Motif</Label>
                    <select
                      value={formData.rdv_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, rdv_type: e.target.value }))}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="">-- Sélectionner --</option>
                      <option value="absence">Absence</option>
                      <option value="performance">Performance</option>
                      <option value="disciplinaire">Disciplinaire</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>

                {/* Mode */}
                <div className="space-y-2">
                  <Label className="font-semibold">Mode *</Label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: 'presentiel', label: 'Présentiel', icon: <Building className="w-4 h-4" /> },
                      { value: 'telephonique', label: 'Téléphonique', icon: <Phone className="w-4 h-4" /> },
                      { value: 'visio', label: 'Visioconférence', icon: <Video className="w-4 h-4" /> }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, mode: option.value }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          formData.mode === option.value
                            ? 'border-[#1D4E89] bg-[#1D4E89] text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {option.icon}
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date et heure */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-semibold">Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold">Heure *</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                {/* Lieu (si présentiel) */}
                {formData.mode === 'presentiel' && (
                  <div className="space-y-2">
                    <Label className="font-semibold">Lieu</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="Adresse du rendez-vous"
                    />
                  </div>
                )}

                {/* Téléphone (si téléphonique) */}
                {formData.mode === 'telephonique' && (
                  <div className="space-y-2">
                    <Label className="font-semibold">Téléphone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Numéro de téléphone"
                    />
                  </div>
                )}

                {/* Commentaire */}
                <div className="space-y-2">
                  <Label className="font-semibold">Commentaire / Motif détaillé</Label>
                  <Textarea
                    value={formData.comment}
                    onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Détails du rendez-vous..."
                    rows={3}
                  />
                </div>

                {/* Envoi email */}
                {!editingRdv && (
                  <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      id="send_email"
                      checked={formData.send_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
                      className="w-5 h-5 rounded border-gray-300 text-[#1D4E89] focus:ring-[#1D4E89]"
                    />
                    <label htmlFor="send_email" className="flex-1">
                      <span className="font-medium text-gray-900">Envoyer une convocation par email</span>
                      <p className="text-sm text-gray-500">
                        Un email sera envoyé à {formData.employee_email || '(sélectionner un salarié)'}
                      </p>
                    </label>
                    <Send className="w-5 h-5 text-[#1D4E89]" />
                  </div>
                )}

                {formData.send_email && !formData.employee_email && formData.selected_employee && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    ⚠️ Ce salarié n'a pas d'adresse email. L'email ne pourra pas être envoyé.
                  </p>
                )}

                {/* Boutons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#1D4E89] hover:bg-[#163d6e] text-white flex-1"
                    data-testid="save-rdv-btn"
                  >
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Enregistrement...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {editingRdv ? 'Modifier' : 'Créer le RDV'}
                      </span>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="px-6"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Liste des RDV */}
        <div className="space-y-4">
          {rdvList.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg">Aucun RDV enregistré</p>
                <p className="text-gray-400 text-sm mt-1">Cliquez sur "Nouveau RDV" pour créer un rendez-vous</p>
              </CardContent>
            </Card>
          ) : (
            rdvList.map(rdv => (
              <Card key={rdv.rdv_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    {/* Infos principales */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1D4E89] flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#0F172A]">{rdv.employee_name}</p>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {getEventTypeLabel(rdv.event_type)}
                            </span>
                            {rdv.rdv_type && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                                {getRdvTypeLabel(rdv.rdv_type)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 ml-13">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {new Date(rdv.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {rdv.time}
                        </span>
                        <span className="flex items-center gap-1">
                          {getModeIcon(rdv.mode)}
                          {getModeLabel(rdv.mode)}
                        </span>
                      </div>
                      
                      {rdv.comment && (
                        <p className="text-sm text-gray-500 italic ml-13 mt-2">"{rdv.comment}"</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(rdv)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(rdv.rdv_id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminRDV;
